import { CSSProperties } from 'react';

export const theme = {
  colors: {
    primary: '#634a75',
    primaryLight: '#efffff',
    border: '#bfbfbf',
    text: '#000000',
    textLight: '#bebebe',
    white: '#ffffff',
    transparent: 'transparent',
  },
  spacing: {
    xs: '5px',
    sm: '10px',
    md: '15px',
    lg: '30px',
    xl: '70px',
  },
  borderRadius: {
    sm: '3px',
    md: '6px',
  },
  fontSize: {
    xs: '12px',
    sm: '13px',
    md: '16px',
    lg: '18px',
  },
  fontFamily: {
    primary: 'Roboto Condensed',
    secondary: '"Roboto Condensed",Helvetica,Arial,"sans-serif"',
  },
  breakpoints: {
    minWidth: '500px',
  },
} as const;

interface BaseStyles {
  app: CSSProperties;
  form: CSSProperties;
  clientReg: CSSProperties;
  centeredContainer: CSSProperties;
}

interface InputStyles {
  input: CSSProperties;
  regForm: CSSProperties;
  pin: CSSProperties;
  check: CSSProperties;
}

interface ButtonStyles {
  submit: CSSProperties;
  submitSmall: CSSProperties;
  submitLarge: CSSProperties;
  addScript: CSSProperties;
  resetSmall: CSSProperties;
}

interface TextStyles {
  span: CSSProperties;
  instruction: CSSProperties;
  link: CSSProperties;
  terms: CSSProperties;
}

interface LayoutStyles {
  dropdown: CSSProperties;
  divFlexCentered: CSSProperties;
}

export const baseStyles: BaseStyles = {
  app: {
    display: 'flex',
    height: 'calc(100vh - 60px)',
  },
  form: {
    margin: 'auto',
    justifyContent: 'center',
    justifyItems: 'center',
    padding: theme.spacing.sm,
    width: '50%',
    minWidth: theme.breakpoints.minWidth,
    textAlign: 'center',
    display: 'flex',
    minHeight: theme.breakpoints.minWidth,
    flexDirection: 'column',
  },
  clientReg: {
    marginLeft: 'auto',
    marginRight: 'auto',
    justifyContent: 'center',
    justifyItems: 'center',
    padding: theme.spacing.sm,
    width: '50%',
    minWidth: theme.breakpoints.minWidth,
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
  },
  centeredContainer: {
    margin: 'auto',
    justifyContent: 'center',
    justifyItems: 'center',
    padding: theme.spacing.sm,
    width: '50%',
    minWidth: theme.breakpoints.minWidth,
    textAlign: 'center',
    display: 'flex',
    minHeight: theme.breakpoints.minWidth,
    flexDirection: 'column',
  },
};

export const inputStyles: InputStyles = {
  input: {
    margin: `${theme.spacing.lg} auto ${theme.spacing.sm} auto`,
    padding: theme.spacing.md,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: theme.borderRadius.md,
    boxSizing: 'border-box',
    textAlign: 'center',
    fontFamily: theme.fontFamily.primary,
    fontSize: theme.fontSize.lg,
    width: '280px',
  },
  regForm: {
    margin: `${theme.spacing.lg} ${theme.spacing.sm} ${theme.spacing.sm} ${theme.spacing.sm}`,
    padding: theme.spacing.md,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: theme.borderRadius.md,
    boxSizing: 'border-box',
    textAlign: 'center',
    fontFamily: theme.fontFamily.primary,
    fontSize: theme.fontSize.lg,
    width: '250px',
  },
  pin: {
    margin: `${theme.spacing.lg} auto ${theme.spacing.sm} auto`,
    padding: theme.spacing.md,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: theme.borderRadius.md,
    boxSizing: 'border-box',
    textAlign: 'center',
    fontFamily: theme.fontFamily.primary,
    fontSize: theme.fontSize.lg,
    width: '100px',
  },
  check: {
    margin: `0px auto ${theme.spacing.sm} auto`,
    padding: theme.spacing.xs,
    textAlign: 'center',
    minWidth: theme.breakpoints.minWidth,
    fontFamily: theme.fontFamily.primary,
    fontSize: theme.fontSize.lg,
    width: '40%',
  },
};

export const buttonStyles: ButtonStyles = {
  submit: {
    margin: `${theme.spacing.lg} auto 0 auto`,
    padding: `${theme.spacing.sm} ${theme.spacing.sm}`,
    border: `1px solid ${theme.colors.primaryLight}`,
    borderRadius: theme.borderRadius.md,
    background: theme.colors.primary,
    minWidth: '100px',
    width: '16%',
    fontSize: theme.fontSize.lg,
    color: theme.colors.white,
    cursor: 'pointer',
  },
  submitSmall: {
    margin: `0px 3px 0 auto`,
    padding: `6px 6px`,
    border: `1px solid ${theme.colors.primary}`,
    borderRadius: theme.borderRadius.md,
    background: theme.colors.primary,
    minWidth: '60px',
    width: '5%',
    fontSize: theme.fontSize.sm,
    color: theme.colors.white,
    cursor: 'pointer',
  },
  submitLarge: {
    margin: `${theme.spacing.lg} auto 0 auto`,
    padding: `${theme.spacing.sm} ${theme.spacing.sm}`,
    border: `1px solid ${theme.colors.primaryLight}`,
    borderRadius: theme.borderRadius.md,
    background: theme.colors.primary,
    minWidth: '100px',
    width: '200px',
    fontSize: theme.fontSize.lg,
    color: theme.colors.white,
    cursor: 'pointer',
  },
  addScript: {
    margin: `0px auto 0 auto`,
    padding: `6px 6px`,
    border: `1px solid ${theme.colors.primaryLight}`,
    borderRadius: theme.borderRadius.md,
    background: theme.colors.primary,
    minWidth: '100px',
    width: '60%',
    fontSize: theme.fontSize.md,
    color: theme.colors.white,
    cursor: 'pointer',
  },
  resetSmall: {
    margin: `0px 3px 0 auto`,
    padding: `6px 6px`,
    border: `1px solid ${theme.colors.primary}`,
    borderRadius: theme.borderRadius.md,
    background: theme.colors.transparent,
    minWidth: '60px',
    width: '5%',
    fontSize: theme.fontSize.sm,
    color: theme.colors.primary,
    cursor: 'pointer',
  },
};

export const textStyles: TextStyles = {
  span: {
    margin: `0 0 0 ${theme.spacing.sm}`,
  },
  instruction: {
    margin: `0 0 ${theme.spacing.md} 0`,
  },
  link: {
    margin: `${theme.spacing.md} 0 0 ${theme.spacing.sm}`,
    fontSize: theme.fontSize.xs,
    color: 'rgb(13, 110, 253)',
    textDecoration: 'underline',
  },
  terms: {
    margin: `${theme.spacing.lg} auto ${theme.spacing.sm} auto`,
    padding: theme.spacing.xs,
    boxSizing: 'border-box',
    textAlign: 'left',
    fontFamily: theme.fontFamily.primary,
    fontSize: theme.fontSize.xs,
    width: '400px',
  },
};

export const layoutStyles: LayoutStyles = {
  dropdown: {
    width: '100%',
  },
  divFlexCentered: {
    flex: '1',
    textAlign: 'center',
  },
};

export const createButtonStyle = (
  variant: 'primary' | 'secondary' | 'small' | 'large' = 'primary',
): CSSProperties => {
  const baseButton = {
    border: `1px solid ${theme.colors.primary}`,
    borderRadius: theme.borderRadius.md,
    fontFamily: theme.fontFamily.primary,
    cursor: 'pointer',
    transition: 'all 0.2s ease-in-out',
  };

  switch (variant) {
    case 'primary':
      return {
        ...baseButton,
        ...buttonStyles.submit,
      };
    case 'secondary':
      return {
        ...baseButton,
        ...buttonStyles.resetSmall,
      };
    case 'small':
      return {
        ...baseButton,
        ...buttonStyles.submitSmall,
      };
    case 'large':
      return {
        ...baseButton,
        ...buttonStyles.submitLarge,
      };
    default:
      return {
        ...baseButton,
        ...buttonStyles.submit,
      };
  }
};

export const createInputStyle = (
  variant: 'default' | 'form' | 'pin' = 'default',
): CSSProperties => {
  const baseInput = {
    border: `1px solid ${theme.colors.border}`,
    borderRadius: theme.borderRadius.md,
    boxSizing: 'border-box' as const,
    textAlign: 'center' as const,
    fontFamily: theme.fontFamily.primary,
    fontSize: theme.fontSize.lg,
  };

  switch (variant) {
    case 'form':
      return {
        ...baseInput,
        ...inputStyles.regForm,
      };
    case 'pin':
      return {
        ...baseInput,
        ...inputStyles.pin,
      };
    default:
      return {
        ...baseInput,
        ...inputStyles.input,
      };
  }
};

export const dropdownStyle = layoutStyles.dropdown;
export const submitStyle = buttonStyles.submitLarge;
export const submitStyleSmall = buttonStyles.submitSmall;
export const addScriptStyle = buttonStyles.addScript;
export const formStyle = baseStyles.clientReg;
export const checkboxContainer = inputStyles.check;
export const link = textStyles.link;
export const terms = textStyles.terms;

export const authStyles = {
  ...baseStyles,
  ...inputStyles,
  ...buttonStyles,
  ...textStyles,
  ...layoutStyles,
};

export default authStyles;
